import { ActivityCategory } from "../types";

/**
 * Enhanced category detection based on activity content
 */

interface CategoryKeywords {
  [key: string]: {
    keywords: string[];
    priority: number;
    contextualKeywords: string[]; // Location-specific or context-specific keywords
  };
}

const ENHANCED_CATEGORY_KEYWORDS: CategoryKeywords = {
  [ActivityCategory.Movies]: {
    keywords: [
      "movie",
      "film",
      "cinema",
      "theater",
      "theatre",
      "screening",
      "watch",
      "netflix",
      "show",
      "series",
      "documentary",
      "premiere",
      "imax",
      "blockbuster",
      "streaming",
      "episode",
      "season",
      "tv show",
      "sitcom",
      "drama",
      "comedy",
      "horror",
      "action",
      "romance",
      "thriller",
      "animation",
      "cartoon",
    ],
    priority: 3, // Higher priority for Movies
    contextualKeywords: [
      "popcorn",
      "tickets",
      "showtimes",
      "multiplex",
      "drive-in",
      "red carpet",
      "trailer",
      "cast",
      "director",
    ],
  },

  [ActivityCategory.Dining]: {
    keywords: [
      "dinner",
      "lunch",
      "breakfast",
      "brunch",
      "meal",
      "eat",
      "restaurant",
      "food",
      "cuisine",
      "dine",
      "chef",
      "menu",
      "taste",
      "flavor",
      "delicious",
      "bistro",
      "cafeteria",
      "buffet",
      "takeout",
      "delivery",
      "cooking",
      "recipe",
      "gourmet",
      "culinary",
      "gastronomy",
      "seafood",
      "vegetarian",
      "vegan",
    ],
    priority: 3, // Higher priority for Dining
    contextualKeywords: [
      "reservation",
      "michelin",
      "fusion",
      "organic",
      "farm-to-table",
      "tasting",
      "appetizer",
      "dessert",
      "wine pairing",
    ],
  },

  [ActivityCategory.Games]: {
    keywords: [
      "game",
      "board game",
      "card game",
      "trivia",
      "escape room",
      "arcade",
      "video game",
      "gaming",
      "play",
      "puzzle",
      "chess",
      "monopoly",
      "scrabble",
      "poker",
      "quiz",
      "karaoke",
      "bowling",
      "billiards",
      "pool",
      "darts",
      "bingo",
      "charades",
      "console",
      "playstation",
      "xbox",
      "nintendo",
      "pc gaming",
      "mobile game",
    ],
    priority: 2, // Keep moderate priority for Games
    contextualKeywords: [
      "multiplayer",
      "tournament",
      "leaderboard",
      "competition",
      "strategy",
      "high score",
      "level up",
      "achievement",
    ],
  },

  [ActivityCategory.Outdoor]: {
    keywords: [
      "hike",
      "hiking",
      "beach",
      "park",
      "outdoor",
      "nature",
      "walk",
      "walking",
      "run",
      "running",
      "cycle",
      "cycling",
      "bike",
      "biking",
      "sport",
      "sports",
      "swim",
      "swimming",
      "tennis",
      "football",
      "soccer",
      "basketball",
      "trail",
      "mountain",
      "forest",
      "lake",
      "river",
      "camping",
      "picnic",
      "adventure",
      "kayaking",
      "surfing",
      "skiing",
      "climbing",
      "fishing",
      "gardening",
    ],
    priority: 3, // Higher priority for Outdoor
    contextualKeywords: [
      "fresh air",
      "scenic",
      "wildlife",
      "sunrise",
      "sunset",
      "panoramic",
      "expedition",
      "wilderness",
      "eco-friendly",
    ],
  },

  [ActivityCategory.Drinks]: {
    keywords: [
      "coffee",
      "drink",
      "drinks",
      "bar",
      "pub",
      "cocktail",
      "cocktails",
      "wine",
      "beer",
      "tea",
      "cafe",
      "brewery",
      "happy hour",
      "latte",
      "cappuccino",
      "espresso",
      "smoothie",
      "juice",
      "milkshake",
      "boba",
      "bubble tea",
    ],
    priority: 2,
    contextualKeywords: [
      "craft",
      "artisan",
      "roast",
      "vintage",
      "mixology",
      "sommelier",
    ],
  },

  [ActivityCategory.Fitness]: {
    keywords: [
      "gym",
      "workout",
      "exercise",
      "fitness",
      "yoga",
      "pilates",
      "dance",
      "dancing",
      "martial arts",
      "crossfit",
      "training",
      "cardio",
      "weights",
      "strength",
      "aerobics",
      "zumba",
      "spinning",
      "cycling class",
      "bootcamp",
      "meditation",
      "stretching",
      "bodybuilding",
      "calisthenics",
      "boxing",
    ],
    priority: 3, // Higher priority for Fitness
    contextualKeywords: [
      "personal trainer",
      "muscle",
      "endurance",
      "flexibility",
      "wellness",
      "nutrition",
      "recovery",
      "mindfulness",
    ],
  },

  [ActivityCategory.General]: {
    keywords: [
      "meetup",
      "hangout",
      "social",
      "gathering",
      "event",
      "activity",
      "fun",
      "chill",
      "chat",
      "talk",
      "discussion",
      "meeting",
      "get together",
    ],
    priority: 1,
    contextualKeywords: [
      "networking",
      "friendship",
      "community",
      "casual",
      "informal",
    ],
  },
};

/**
 * Detect the most appropriate category for an activity based on its content
 */
export function detectActivityCategory(
  title: string = "",
  description: string = "",
  location: string = "",
): ActivityCategory {
  // First, check if any input already contains a category hint (like emoji prefixes from AI)
  const allInputs = [title, description, location].join(" ").toLowerCase();

  // Check for emoji-prefixed categories (common in AI responses) - Enhanced patterns
  const emojiCategoryPatterns = [
    {
      pattern:
        /�|�|�|�|�|�️|📽️|movie|film|cinema|theater|theatre|screening|watch/i,
      category: ActivityCategory.Movies,
    },
    {
      pattern:
        /🍽️|🍕|🍜|🥘|🍲|🥗|🍱|🥙|dining|food|restaurant|meal|eat|chef|cook/i,
      category: ActivityCategory.Dining,
    },
    {
      pattern:
        /�|�️|🚶|🚴|⛰️|�️|�️|🌊|⛺|🥾|outdoor|nature|hike|beach|park|camping|adventure/i,
      category: ActivityCategory.Outdoor,
    },
    {
      pattern:
        /☕|🍻|🍷|🥤|🍸|🍹|🥃|🍺|🧋|drink|coffee|bar|cocktail|wine|beer|cafe/i,
      category: ActivityCategory.Drinks,
    },
    {
      pattern:
        /💪|🏋️|🧘|🤸|🏃|🚴‍♀️|🏊|⛹️|fitness|workout|gym|yoga|exercise|training|sport/i,
      category: ActivityCategory.Fitness,
    },
    {
      pattern:
        /🎮|🎯|🎲|🃏|🎪|🎨|🎵|🎶|game|gaming|play|board|card|arcade|video|quiz|trivia/i,
      category: ActivityCategory.Games,
    },
  ];

  for (const { pattern, category } of emojiCategoryPatterns) {
    if (pattern.test(allInputs)) {
      return category;
    }
  }

  const combinedText = `${title} ${description} ${location}`.toLowerCase();

  let bestMatch: ActivityCategory = ActivityCategory.General; // Default fallback
  let highestScore = 0;

  // Score each category based on keyword matches
  for (const [category, data] of Object.entries(ENHANCED_CATEGORY_KEYWORDS)) {
    let score = 0;

    // Check primary keywords
    data.keywords.forEach((keyword) => {
      const keywordCount = (combinedText.match(new RegExp(keyword, "g")) || [])
        .length;
      score += keywordCount * data.priority;
    });

    // Bonus points for contextual keywords
    data.contextualKeywords.forEach((keyword) => {
      if (combinedText.includes(keyword)) {
        score += data.priority * 0.5; // Half weight for contextual keywords
      }
    });

    // Title matches get extra weight
    if (title) {
      const titleLower = title.toLowerCase();
      data.keywords.forEach((keyword) => {
        if (titleLower.includes(keyword)) {
          score += data.priority * 1.5; // 1.5x weight for title matches
        }
      });
    }

    if (score > highestScore) {
      highestScore = score;
      bestMatch = category as ActivityCategory;
    }
  }

  // Additional heuristics based on location context
  if (highestScore === 0 && location) {
    bestMatch = detectCategoryFromLocation(location);
  }

  return bestMatch;
}

/**
 * Detect category based on location context
 */
function detectCategoryFromLocation(location: string): ActivityCategory {
  const locationLower = location.toLowerCase();

  // Location-based category detection
  const locationPatterns = [
    {
      keywords: ["mall", "shopping", "cinema", "theater"],
      category: ActivityCategory.Movies,
    },
    {
      keywords: ["restaurant", "kitchen", "dining", "food court"],
      category: ActivityCategory.Dining,
    },
    {
      keywords: ["park", "garden", "beach", "trail", "mountain", "lake"],
      category: ActivityCategory.Outdoor,
    },
    {
      keywords: ["bar", "pub", "cafe", "coffee", "brewery", "lounge"],
      category: ActivityCategory.Drinks,
    },
    {
      keywords: ["gym", "fitness", "studio", "court", "pool", "sports"],
      category: ActivityCategory.Fitness,
    },
    {
      keywords: ["arcade", "entertainment", "gaming", "bowling"],
      category: ActivityCategory.Games,
    },
  ];

  for (const pattern of locationPatterns) {
    if (pattern.keywords.some((keyword) => locationLower.includes(keyword))) {
      return pattern.category;
    }
  }

  return ActivityCategory.General; // Default fallback
}

/**
 * Get confidence level of the category detection (0-100)
 */
export function getCategoryConfidence(
  title: string = "",
  description: string = "",
  location: string = "",
  detectedCategory: ActivityCategory,
): number {
  const combinedText = `${title} ${description} ${location}`.toLowerCase();
  const categoryData = ENHANCED_CATEGORY_KEYWORDS[detectedCategory];

  if (!categoryData) return 0;

  let matchCount = 0;
  let totalKeywords = categoryData.keywords.length;

  categoryData.keywords.forEach((keyword) => {
    if (combinedText.includes(keyword)) {
      matchCount++;
    }
  });

  // Calculate confidence as percentage of matched keywords
  const confidence = Math.min(100, (matchCount / totalKeywords) * 100 * 2); // Scale up for better UX

  return Math.round(confidence);
}

/**
 * Suggest multiple potential categories with confidence scores
 */
export function suggestCategories(
  title: string = "",
  description: string = "",
  location: string = "",
): Array<{ category: ActivityCategory; confidence: number }> {
  const suggestions: Array<{ category: ActivityCategory; confidence: number }> =
    [];

  for (const category of Object.values(ActivityCategory)) {
    const confidence = getCategoryConfidence(
      title,
      description,
      location,
      category,
    );
    if (confidence > 10) {
      // Only include categories with reasonable confidence
      suggestions.push({ category, confidence });
    }
  }

  // Sort by confidence descending
  return suggestions.sort((a, b) => b.confidence - a.confidence);
}
